import './styles.css';
import {
  createInitialState,
  DEFAULT_ROTATION,
  DEFAULT_TILT,
  DEFAULT_ZOOM,
  optimalHeight,
  reduceGame,
  serializeState,
} from './core/game';
import { detectHeightCrossings, hardHeightLimit, maxBrushSize, minBrushSize } from './core/brush';
import { baseLevels, createRandomLevel, getLevel, RANDOM_LEVEL_ID, RANDOM_LEVEL_NAME } from './core/levels';
import type { BrushMode, GameAction, GameHarness, GameState, HarnessSnapshot, Level } from './core/types';
import { createSession } from './platform/session';
import { pickTile } from './platform/picking';
import { renderGrid } from './platform/renderer';
import { randomSeed } from './platform/randomSeed';
import { formatElapsedTime } from './platform/timer';
import { createAnimationLoop, createFlashLoop } from './platform/animations';
import { bindInput } from './platform/input';
import type { GameIntent } from './platform/input';


/** How long (ms) the "failed" message stays up before the playing area auto-resets. Also the duration of the fail-flash pulse animation (iteration 16). */
const FAIL_RESET_DELAY_MS = 800;

/**
 * Multiplies the actual height-change rate applied by the continuous
 * hold-to-paint loop, at every brush speed slider setting (the slider's
 * displayed 0-100% range and `state.brushSpeed` itself are unchanged; only
 * the real-world drawing rate they produce is doubled). Iteration 12.
 */
const DRAWING_SPEED_MULTIPLIER = 2;

/**
 * Length of the play-time countdown shown next to "Moved" (iteration 18):
 * a real per-attempt time limit. Reaching 0 fails the attempt exactly like
 * a height-boundary violation (same red flash + auto-reset), via the
 * TIMEOUT action. Only ticks down while actively 'playing' — paused while
 * viewing a finished/failed screen.
 */
/**
 * Computes the time limit (in milliseconds) for a level based on its grid
 * size. Linear scaling: 6 minutes for 7×7, ~12 minutes for 25×25.
 * Formula: 329375 + 625 * size².
 */
function timerCapForLevel(level: Level): number {
  return 329375 + 625 * level.size * level.size;
}

const TIMER_TICK_MS = 250;

const board = getElement('#board', HTMLCanvasElement);
const ctx = board.getContext('2d');
if (!ctx) {
  throw new Error('Canvas 2D context is not available.');
}

const appElement = getElement('#app', HTMLElement);
const sidebarElement = getElement('.sidebar', HTMLElement);
const sidebarToggle = getElement('[data-testid="sidebar-toggle"]', HTMLButtonElement);
const fullframeToggle = getElement('[data-testid="fullframe-toggle"]', HTMLButtonElement);
const planeToggle = getElement('[data-testid="plane-toggle"]', HTMLButtonElement);
const shortcutsToggle = getElement('[data-testid="shortcuts-toggle"]', HTMLButtonElement);
const shortcutsBackdrop = getElement('[data-testid="shortcuts-backdrop"]', HTMLElement);
const shortcutsClose = getElement('[data-testid="shortcuts-close"]', HTMLButtonElement);
const zoomToggle = getElement('[data-testid="zoom-toggle"]', HTMLButtonElement);
const zoomPanel = getElement('[data-testid="zoom-panel"]', HTMLElement);
const tiltToggle = getElement('[data-testid="tilt-toggle"]', HTMLButtonElement);
const tiltPanel = getElement('[data-testid="tilt-panel"]', HTMLElement);
const boardWrap = getElement('.board-wrap', HTMLElement);
const levelLabel = getElement('[data-testid="level-label"]', HTMLElement);
const statusLabel = getElement('[data-testid="status-label"]', HTMLElement);
const scoreLabel = getElement('[data-testid="score-label"]', HTMLElement);
const efficiencyLabel = getElement('[data-testid="efficiency-label"]', HTMLElement);
const strokeLabel = getElement('[data-testid="stroke-label"]', HTMLElement);
const timerLabel = getElement('[data-testid="timer-label"]', HTMLElement);
const liftButton = getElement('[data-testid="lift-button"]', HTMLButtonElement);
const lowerButton = getElement('[data-testid="lower-button"]', HTMLButtonElement);
const brushSizeInput = getElement('#brush-size', HTMLInputElement);
const brushSizeValue = getElement('#brush-size-value', HTMLSpanElement);
const brushSpeedInput = getElement('#brush-speed', HTMLInputElement);
const brushSpeedValue = getElement('#brush-speed-value', HTMLSpanElement);
const zoomInput = getElement('#zoom', HTMLInputElement);
const zoomValue = getElement('#zoom-value', HTMLSpanElement);
const tiltInput = getElement('#tilt', HTMLInputElement);
const tiltValue = getElement('#tilt-value', HTMLSpanElement);
const rotateLeftButton = getElement('[data-testid="rotate-left-button"]', HTMLButtonElement);
const rotateRightButton = getElement('[data-testid="rotate-right-button"]', HTMLButtonElement);
const finishButton = getElement('[data-testid="finish-button"]', HTMLButtonElement);
const resetButton = getElement('[data-testid="reset-button"]', HTMLButtonElement);
const levelListElement = getElement('#level-list', HTMLUListElement);
const scoreboardElement = getElement('#scoreboard', HTMLUListElement);
const helpButton = getElement('[data-testid="help-button"]', HTMLButtonElement);
const helpBackdrop = getElement('[data-testid="help-backdrop"]', HTMLElement);
const helpClose = getElement('[data-testid="help-close"]', HTMLButtonElement);
const controlsPrimary = getElement('.controls-primary', HTMLElement);
const captureButton = getElement('[data-testid="capture-button"]', HTMLButtonElement);
const downloadButton = getElement('[data-testid="download-button"]', HTMLButtonElement);
const screenshotBackdrop = getElement('[data-testid="screenshot-backdrop"]', HTMLElement);
const screenshotClose = getElement('[data-testid="screenshot-close"]', HTMLButtonElement);
const screenshotPreviewCanvas = getElement('[data-testid="screenshot-preview"]', HTMLCanvasElement);
const screenshotCopyBtn = getElement('[data-testid="screenshot-copy"]', HTMLButtonElement);
const screenshotDownloadBtn = getElement('[data-testid="screenshot-download"]', HTMLButtonElement);
const screenshotShareBtn = getElement('[data-testid="screenshot-share"]', HTMLButtonElement);
const screenshotStatsToggle = getElement('[data-testid="screenshot-stats-toggle"]', HTMLButtonElement);

const session = createSession();

let state: GameState = createInitialState(getLevel(0));
let isPointerDown = false;
let paintTile: { x: number; y: number } | null = null;
let failResetTimer: ReturnType<typeof setTimeout> | null = null;
let sidebarCollapsed = false;
let fullframe = false;
let showOptimalPlane = false;
/** True while the "S" key is held; restores legacy snap-to-block-center brush targeting (see tileAt/pickTile). */
let snapKeyHeld = false;
/** Milliseconds remaining on the current attempt's time limit; only ticks down while state.status === 'playing' (see updateTimer). */
let timerRemainingMs = timerCapForLevel(state.level);
/** Wall-clock timestamp of the last updateTimer tick, used to compute each tick's elapsed delta. */
let lastTimerTickMs = performance.now();

// --- Timer warning state ---
/** Thresholds (in ms remaining) at which to fire a warning. */
const TIMER_WARNING_THRESHOLDS = [30_000, 15_000, 5_000];
/** Set of thresholds already fired this attempt (cleared on reset/new level). */
const timerWarningsFired = new Set<number>();
/** Duration of the warning flash (double pulse). */
const WARNING_FLASH_DURATION_MS = 500;

const warningFlash = createFlashLoop(
  WARNING_FLASH_DURATION_MS,
  (progress) => {
    const intensity = Math.abs(Math.sin(progress * Math.PI * 2)) * 0.5;
    renderWithWarningFlash(intensity);
  },
  () => render(),
);

// --- Height-match flash state (iteration 33) ---
/** Duration of the height-match glow pulse. */
const MATCH_FLASH_DURATION_MS = 250;
/** Maps tile index → performance.now() timestamp when the flash started. */
const matchFlashTiles = new Map<number, number>();

const matchFlashLoop = createAnimationLoop((_, now) => {
  for (const [idx, start] of matchFlashTiles) {
    if (now - start > MATCH_FLASH_DURATION_MS) matchFlashTiles.delete(idx);
  }
  if (matchFlashTiles.size === 0) { render(); return false; }
  render();
  return true;
});

// --- Continuous rotation animation state ---
/** Rotation speed: radians per millisecond (π/2 per 800ms). Used for key-held continuous rotation. */
const ROTATION_SPEED = (Math.PI / 2) / 800;
/** Button-click rotation speed: radians per millisecond (π/6 in 400ms). */
const BUTTON_ROTATION_SPEED = (Math.PI / 6) / 400;
/** Direction of key-held continuous rotation, or null when no rotation key is held. */
let rotationKeyDir: 'cw' | 'ccw' | null = null;
/** Target angle for button-triggered animated rotation, or null when no button animation is active. */
let rotationButtonTarget: number | null = null;
/** Direction of the current button-triggered rotation animation. */
let rotationButtonDir: 'cw' | 'ccw' | null = null;

render();
renderLevelList();
renderScoreboard();
resizeCanvas();
updateTimer();
setInterval(updateTimer, TIMER_TICK_MS);

sidebarToggle.addEventListener('click', () => {
  sidebarCollapsed = !sidebarCollapsed;
  sidebarElement.classList.toggle('collapsed', sidebarCollapsed);
  sidebarToggle.setAttribute('aria-pressed', String(sidebarCollapsed));
});
fullframeToggle.addEventListener('click', () => {
  toggleFullframe();
});
planeToggle.addEventListener('click', () => {
  showOptimalPlane = !showOptimalPlane;
  planeToggle.setAttribute('aria-pressed', String(showOptimalPlane));
  render();
});
bindModalToggle(shortcutsToggle, shortcutsBackdrop, shortcutsClose);
bindPanelToggle(zoomToggle, zoomPanel);
bindPanelToggle(tiltToggle, tiltPanel);
liftButton.addEventListener('click', () => dispatch({ type: 'SET_BRUSH_MODE', mode: 'lift' }));
lowerButton.addEventListener('click', () => dispatch({ type: 'SET_BRUSH_MODE', mode: 'lower' }));
brushSizeInput.addEventListener('input', () => {
  dispatch({ type: 'SET_BRUSH_SIZE', size: Number(brushSizeInput.value) });
});
brushSpeedInput.addEventListener('input', () => {
  dispatch({ type: 'SET_BRUSH_SPEED', speed: Number(brushSpeedInput.value) / 100 });
});
zoomInput.addEventListener('input', () => {
  dispatch({ type: 'SET_ZOOM', zoom: Number(zoomInput.value) });
});
tiltInput.addEventListener('input', () => {
  dispatch({ type: 'SET_TILT', tilt: Number(tiltInput.value) });
});
rotateLeftButton.addEventListener('click', () => startButtonRotation('ccw'));
rotateRightButton.addEventListener('click', () => startButtonRotation('cw'));
finishButton.addEventListener('click', () => {
  dispatch({ type: 'FINISH' });
  persistScore();
  maybeAutoCaptureBoard();
});
resetButton.addEventListener('click', () => performReset());

// Help & Shortcuts modal open/close (iteration 50: shortcuts moved from an
// inline reveal-panel to a Help-style overlay, sharing the same behavior).
bindModalToggle(helpButton, helpBackdrop, helpClose);

// --- Screenshot system (iteration 51) ---

type CaptureData = {
  blob: Blob;
  dataUrl: string;
  stats: { levelName: string; score: string; efficiency: string; moved: string; time: string };
};

let lastCapture: CaptureData | null = null;
const offscreenCanvas = document.createElement('canvas');

/**
 * Auto-captures a "memory" screenshot of the just-finished board whenever an
 * attempt ends (Finish button or TIMEOUT) with a score of 90% or better.
 * Must only be called *after* the terminal action (FINISH/TIMEOUT) has been
 * dispatched, so `state.score` reflects the freshly computed final score —
 * calling it beforehand captures a stale/uncalculated score.
 */
function maybeAutoCaptureBoard(): void {
  if (state.score !== null && state.score >= 0.9) {
    captureBoard();
  }
}

function captureBoard(): void {
  // Render the board at default camera, no overlays
  offscreenCanvas.width = 2000;
  offscreenCanvas.height = 2000;
  const offCtx = offscreenCanvas.getContext('2d')!;
  renderGrid(offCtx, offscreenCanvas.width, offscreenCanvas.height, {
    size: state.level.size,
    heightLimit: hardHeightLimit(state.level),
    optimalHeight: showOptimalPlane ? optimalHeight(state.level.heights) : undefined,
    heights: state.heights,
    rotation: DEFAULT_ROTATION,
    hoverTile: null,
    brushSize: undefined,
    bottomPadding: 0,
    zoom: DEFAULT_ZOOM,
    tilt: DEFAULT_TILT,
  });

  const stats = {
    levelName: state.level.name,
    score: formatScore(state.score),
    efficiency: formatScore(state.efficiency),
    moved: state.totalHeightChange.toFixed(1),
    time: timerLabel.textContent?.replace('Time: ', '') ?? '',
  };

  offscreenCanvas.toBlob((blob) => {
    if (!blob) return;
    const dataUrl = offscreenCanvas.toDataURL('image/png');
    lastCapture = { blob, dataUrl, stats };
    downloadButton.disabled = false;
    showScreenshotPreview();
  }, 'image/png');
}

function showScreenshotPreview(): void {
  if (!lastCapture) return;
  // Draw the image onto the preview canvas
  const img = new Image();
  img.onload = () => {
    screenshotPreviewCanvas.width = 800;
    screenshotPreviewCanvas.height = 800;
    const previewCtx = screenshotPreviewCanvas.getContext('2d')!;
    previewCtx.drawImage(img, 0, 0, screenshotPreviewCanvas.width, screenshotPreviewCanvas.height);
    if (screenshotStatsToggle.getAttribute('aria-pressed') === 'true') drawStatsOverlay(previewCtx);
  };
  img.src = lastCapture.dataUrl;

  // Show/hide share button based on API support
  screenshotShareBtn.style.display = ('share' in navigator) ? '' : 'none';
  screenshotStatsToggle.setAttribute('aria-pressed', 'false');

  screenshotBackdrop.classList.remove('hidden');
  screenshotBackdrop.setAttribute('aria-hidden', 'false');
}

function closeScreenshotPreview(): void {
  screenshotBackdrop.classList.add('hidden');
  screenshotBackdrop.setAttribute('aria-hidden', 'true');
}

function drawStatsOverlay(previewCtx: CanvasRenderingContext2D): void {
  if (!lastCapture) return;
  const { stats } = lastCapture;
  const w = screenshotPreviewCanvas.width;
  const h = screenshotPreviewCanvas.height;
  const fontSize = Math.round(w / 28);
  const padding = fontSize * 0.6;
  const lineHeight = fontSize * 1.4;
  const lines = [
    stats.levelName,
    `Score: ${stats.score}  Efficiency: ${stats.efficiency}`,
    `Moved: ${stats.moved}  Time: ${stats.time}`,
  ];
  const boxHeight = padding * 2 + lines.length * lineHeight;

  previewCtx.save();
  previewCtx.fillStyle = 'rgba(21, 21, 19, 0.8)';
  previewCtx.fillRect(0, h - boxHeight, w, boxHeight);
  previewCtx.font = `bold ${fontSize}px 'Fira Code', monospace`;
  previewCtx.fillStyle = '#e0dcd4';
  previewCtx.textBaseline = 'top';
  for (let i = 0; i < lines.length; i++) {
    previewCtx.fillText(lines[i], padding, h - boxHeight + padding + i * lineHeight);
  }
  previewCtx.restore();
}

function redrawPreview(): void {
  if (!lastCapture) return;
  const previewCtx = screenshotPreviewCanvas.getContext('2d')!;
  previewCtx.clearRect(0, 0, screenshotPreviewCanvas.width, screenshotPreviewCanvas.height);
  const img = new Image();
  img.onload = () => {
    previewCtx.drawImage(img, 0, 0, screenshotPreviewCanvas.width, screenshotPreviewCanvas.height);
    if (screenshotStatsToggle.getAttribute('aria-pressed') === 'true') drawStatsOverlay(previewCtx);
  };
  img.src = lastCapture.dataUrl;
}

/** Produce a full-resolution export blob, compositing stats when toggle is on. */
function getExportBlob(): Promise<Blob | null> {
  if (!lastCapture) return Promise.resolve(null);
  const statsOn = screenshotStatsToggle.getAttribute('aria-pressed') === 'true';
  if (!statsOn) return Promise.resolve(lastCapture.blob);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = img.width;
      exportCanvas.height = img.height;
      const ctx = exportCanvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      // Draw stats at full resolution
      const w = exportCanvas.width;
      const h = exportCanvas.height;
      const fontSize = Math.round(w / 28);
      const padding = fontSize * 0.6;
      const lineHeight = fontSize * 1.4;
      const lines = [
        lastCapture!.stats.levelName,
        `Score: ${lastCapture!.stats.score}  Efficiency: ${lastCapture!.stats.efficiency}`,
        `Moved: ${lastCapture!.stats.moved}  Time: ${lastCapture!.stats.time}`,
      ];
      const boxHeight = padding * 2 + lines.length * lineHeight;
      ctx.fillStyle = 'rgba(21, 21, 19, 0.8)';
      ctx.fillRect(0, h - boxHeight, w, boxHeight);
      ctx.font = `bold ${fontSize}px 'Fira Code', monospace`;
      ctx.fillStyle = '#e0dcd4';
      ctx.textBaseline = 'top';
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], padding, h - boxHeight + padding + i * lineHeight);
      }
      exportCanvas.toBlob((blob) => resolve(blob), 'image/png');
    };
    img.src = lastCapture!.dataUrl;
  });
}

captureButton.addEventListener('click', captureBoard);

downloadButton.addEventListener('click', () => {
  if (!lastCapture) return;
  const a = document.createElement('a');
  a.href = lastCapture.dataUrl;
  a.download = `perfect-plane-${state.level.name.toLowerCase().replace(/\s+/g, '-')}.png`;
  a.click();
});

screenshotClose.addEventListener('click', closeScreenshotPreview);
screenshotBackdrop.addEventListener('click', (e) => {
  if (e.target === screenshotBackdrop) closeScreenshotPreview();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !screenshotBackdrop.classList.contains('hidden')) closeScreenshotPreview();
});

screenshotCopyBtn.addEventListener('click', async () => {
  const blob = await getExportBlob();
  if (!blob) return;
  try {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
  } catch { /* clipboard not available */ }
});

screenshotDownloadBtn.addEventListener('click', async () => {
  const blob = await getExportBlob();
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `perfect-plane-${state.level.name.toLowerCase().replace(/\s+/g, '-')}.png`;
  a.click();
  URL.revokeObjectURL(url);
});

screenshotShareBtn.addEventListener('click', async () => {
  const blob = await getExportBlob();
  if (!blob) return;
  const file = new File([blob], 'perfect-plane.png', { type: 'image/png' });
  const shareData: ShareData = {
    files: [file],
    text: `Perfect Plane — ${lastCapture!.stats.levelName} ${lastCapture!.stats.score}`,
    url: window.location.href,
  };
  if (navigator.canShare?.(shareData)) {
    await navigator.share(shareData).catch(() => {});
  }
});

screenshotStatsToggle.addEventListener('click', () => {
  const pressed = screenshotStatsToggle.getAttribute('aria-pressed') === 'true';
  screenshotStatsToggle.setAttribute('aria-pressed', String(!pressed));
  redrawPreview();
});

// --- Input binding: all keyboard, pointer, wheel, contextmenu, resize ---
bindInput(
  { board, boardWrap, window },
  (event) => tileAt(event),
  handleIntent,
);

function handleIntent(intent: GameIntent): void {
  switch (intent.type) {
    case 'paint.start':
      if (intent.brushOverride) {
        dispatch({ type: 'SET_BRUSH_MODE', mode: intent.brushOverride });
      }
      isPointerDown = true;
      if (intent.tile) dispatch({ type: 'SET_HOVER', tile: intent.tile });
      paintTile = intent.tile;
      paintLoop.start();
      break;
    case 'paint.move':
      if (isPointerDown) {
        paintTile = intent.tile;
      }
      break;
    case 'paint.end':
      isPointerDown = false;
      paintTile = null;
      paintLoop.stop();
      break;
    case 'hover':
      if (intent.tile?.x !== state.hoverTile?.x || intent.tile?.y !== state.hoverTile?.y) {
        dispatch({ type: 'SET_HOVER', tile: intent.tile });
      }
      break;
    case 'hover.leave':
      dispatch({ type: 'SET_HOVER', tile: null });
      break;
    case 'rotate.start':
      startKeyRotation(intent.dir);
      break;
    case 'rotate.stop':
      stopKeyRotation(intent.dir);
      break;
    case 'speed.start':
      startSpeedChange(intent.dir);
      break;
    case 'speed.stop':
      stopSpeedChange(intent.dir);
      break;
    case 'zoom.start':
      startZoomChange(intent.dir);
      break;
    case 'zoom.stop':
      stopZoomChange(intent.dir);
      break;
    case 'tilt.start':
      startTiltChange(intent.dir);
      break;
    case 'tilt.stop':
      stopTiltChange(intent.dir);
      break;
    case 'snap.down':
      snapKeyHeld = true;
      break;
    case 'snap.up':
      snapKeyHeld = false;
      break;
    case 'toggle.fullframe':
      toggleFullframe();
      break;
    case 'toggle.plane':
      showOptimalPlane = !showOptimalPlane;
      planeToggle.setAttribute('aria-pressed', String(showOptimalPlane));
      render();
      break;
    case 'brush.sizeDelta':
      dispatch({ type: 'SET_BRUSH_SIZE', size: state.brushSize + intent.delta });
      break;
    case 'viewport.resize':
      resizeCanvas();
      break;
  }
}

function resizeCanvas(): void {
  const rect = boardWrap.getBoundingClientRect();
  const width = Math.max(200, Math.floor(rect.width) || board.width);
  const height = Math.max(200, Math.floor(rect.height) || board.height);
  if (board.width !== width || board.height !== height) {
    board.width = width;
    board.height = height;
    render();
  }
}

/** Toggles fullframe mode; shared by the fullframe-toggle button click and the `F` keyboard shortcut. */
function toggleFullframe(): void {
  fullframe = !fullframe;
  appElement.classList.toggle('fullframe', fullframe);
  fullframeToggle.setAttribute('aria-pressed', String(fullframe));
}

/**
 * Wires a view-toolbar icon button to show/hide an adjacent reveal panel
 * (iteration 45): the Zoom and Tilt panels use this same click-to-expand
 * pattern, starting collapsed (`hidden`) and toggling independently of one
 * another. (Shortcuts moved to `bindModalToggle` in iteration 50.)
 */
function bindPanelToggle(toggle: HTMLButtonElement, panel: HTMLElement): void {
  toggle.addEventListener('click', () => {
    const expanding = panel.hidden;
    panel.hidden = !expanding;
    toggle.setAttribute('aria-pressed', String(expanding));
  });
}

/**
 * Wires a toggle button to open/close a centered overlay modal (iteration 50):
 * shared by Help and Shortcuts. Unlike `bindPanelToggle`'s inline reveal
 * panels — which reflow surrounding layout as they expand — a modal renders
 * in a fixed, centered overlay that never shifts other page content, and its
 * size is capped/responsive (`max-width`/`max-height` + `overflow-y: auto`
 * in CSS) so it fits any viewport. Closes via the toggle button again, the
 * modal's own close button, clicking the backdrop, or pressing Escape.
 */
function bindModalToggle(toggle: HTMLButtonElement, backdrop: HTMLElement, closeButton: HTMLButtonElement): void {
  function open(): void {
    backdrop.classList.remove('hidden');
    backdrop.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-pressed', 'true');
  }
  function close(): void {
    backdrop.classList.add('hidden');
    backdrop.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-pressed', 'false');
  }
  toggle.addEventListener('click', () => {
    if (backdrop.classList.contains('hidden')) open();
    else close();
  });
  closeButton.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !backdrop.classList.contains('hidden')) close();
  });
}

// --- Continuous rotation animation ---

function normalizeAngle(a: number): number {
  return ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

function startKeyRotation(dir: 'cw' | 'ccw'): void {
  // Key rotation cancels any in-progress button animation
  rotationButtonTarget = null;
  rotationButtonDir = null;
  rotationKeyDir = dir;
  rotationLoop.start();
}

function stopKeyRotation(dir: 'cw' | 'ccw'): void {
  if (rotationKeyDir === dir) {
    rotationKeyDir = null;
    // If no button animation pending either, loop will stop itself
  }
}

function startButtonRotation(dir: 'cw' | 'ccw'): void {
  // If key is held, ignore button press
  if (rotationKeyDir) return;
  const step = dir === 'cw' ? Math.PI / 6 : -Math.PI / 6;
  rotationButtonTarget = normalizeAngle(state.rotation + step);
  rotationButtonDir = dir;
  rotationLoop.start();
}

const rotationLoop = createAnimationLoop((dt) => {
  let delta = 0;

  if (rotationKeyDir) {
    // Key held: continuous rotation
    delta = ROTATION_SPEED * dt * (rotationKeyDir === 'cw' ? 1 : -1);
  } else if (rotationButtonTarget !== null && rotationButtonDir !== null) {
    // Button-triggered animated rotation toward target
    const maxStep = BUTTON_ROTATION_SPEED * dt;
    const sign = rotationButtonDir === 'cw' ? 1 : -1;
    const remaining = normalizeAngle((rotationButtonTarget - state.rotation) * sign);
    if (remaining <= maxStep || remaining > Math.PI) {
      // Close enough or overshot — snap to target
      delta = normalizeAngle(rotationButtonTarget - state.rotation);
      if (delta > Math.PI) delta -= 2 * Math.PI;
      rotationButtonTarget = null;
      rotationButtonDir = null;
    } else {
      delta = maxStep * sign;
    }
  }

  if (delta !== 0) {
    state = { ...state, rotation: normalizeAngle(state.rotation + delta) };
    render();
  }

  return !!(rotationKeyDir || rotationButtonTarget !== null);
});

// --- Continuous brush speed animation (Q/E / ArrowUp/ArrowDown) ---
/** Speed change rate: full range (0 to 1) in 4 seconds. */
const SPEED_CHANGE_RATE = 0.25; // per second
/** Direction of key-held speed change, or null. */
let speedKeyDir: 'up' | 'down' | null = null;

const speedLoop = createAnimationLoop((dt) => {
  if (speedKeyDir) {
    const delta = (SPEED_CHANGE_RATE * dt) / 1000 * (speedKeyDir === 'up' ? 1 : -1);
    dispatch({ type: 'SET_BRUSH_SPEED', speed: state.brushSpeed + delta });
  }
  return speedKeyDir !== null;
});

function startSpeedChange(dir: 'up' | 'down'): void {
  speedKeyDir = dir;
  speedLoop.start();
}

function stopSpeedChange(dir: 'up' | 'down'): void {
  if (speedKeyDir === dir) {
    speedKeyDir = null;
  }
}

// --- Continuous zoom animation (Q/E) ---
/** Zoom change rate: full 4800-16000 range in ~2 seconds (matches the old 600-2000-in-~2s feel, scaled to the iteration-43 near-isometric range). */
const ZOOM_CHANGE_RATE = 5600; // per second
/** Direction of key-held zoom change, or null. */
let zoomKeyDir: 'out' | 'in' | null = null;

const zoomLoop = createAnimationLoop((dt) => {
  if (zoomKeyDir) {
    const delta = (ZOOM_CHANGE_RATE * dt) / 1000 * (zoomKeyDir === 'in' ? 1 : -1);
    dispatch({ type: 'SET_ZOOM', zoom: state.zoom + delta });
  }
  return zoomKeyDir !== null;
});

function startZoomChange(dir: 'out' | 'in'): void {
  zoomKeyDir = dir;
  zoomLoop.start();
}

function stopZoomChange(dir: 'out' | 'in'): void {
  if (zoomKeyDir === dir) {
    zoomKeyDir = null;
  }
}

// --- Continuous tilt animation (S/W or ArrowDown/ArrowUp) ---
/** Tilt change rate: full 15-65 degree range in ~2 seconds. */
const TILT_CHANGE_RATE = 25; // per second
/** Direction of key-held tilt change, or null. */
let tiltKeyDir: 'down' | 'up' | null = null;

const tiltLoop = createAnimationLoop((dt) => {
  if (tiltKeyDir) {
    const delta = (TILT_CHANGE_RATE * dt) / 1000 * (tiltKeyDir === 'up' ? 1 : -1);
    dispatch({ type: 'SET_TILT', tilt: state.tilt + delta });
  }
  return tiltKeyDir !== null;
});

function startTiltChange(dir: 'down' | 'up'): void {
  tiltKeyDir = dir;
  tiltLoop.start();
}

function stopTiltChange(dir: 'down' | 'up'): void {
  if (tiltKeyDir === dir) {
    tiltKeyDir = null;
  }
}


const paintLoop = createAnimationLoop((dt) => {
  if (!isPointerDown || !paintTile) return false;
  if (state.status === 'failed') { isPointerDown = false; return false; }
  if (state.status === 'playing' || state.status === 'finished') {
    const intensity = state.brushSpeed * (dt / 1000) * DRAWING_SPEED_MULTIPLIER;
    if (intensity > 0) {
      dispatch({ type: 'BRUSH', x: paintTile.x, y: paintTile.y, intensity });
    }
  }
  return true;
});

function tileAt(event: PointerEvent): { x: number; y: number } | null {
  const rect = board.getBoundingClientRect();
  const canvasX = ((event.clientX - rect.left) / rect.width) * board.width;
  const canvasY = ((event.clientY - rect.top) / rect.height) * board.height;
  return pickTile(
    canvasX,
    canvasY,
    board.width,
    board.height,
    {
      size: state.level.size,
      heightLimit: hardHeightLimit(state.level),
      rotation: state.rotation,
      bottomPadding: fullframe ? controlsPrimary.offsetHeight + 32 : 0,
      zoom: state.zoom,
      tilt: state.tilt,
    },
    snapKeyHeld,
  );
}


function dispatch(action: GameAction): HarnessSnapshot {
  const previousStatus = state.status;
  const beforeHeights = action.type === 'BRUSH' ? state.heights : null;
  state = reduceGame(state, action);

  // Height-match flash detection (iteration 33)
  if (action.type === 'BRUSH' && beforeHeights && state.status === 'playing') {
    const size = state.level.size;
    const affected: number[] = [];
    for (let i = 0; i < beforeHeights.length; i += 1) {
      if (beforeHeights[i] !== state.heights[i]) affected.push(i);
    }
    if (affected.length > 0) {
      const crossings = detectHeightCrossings(beforeHeights, state.heights, affected, size);
      if (crossings.size > 0) {
        const now = performance.now();
        for (const idx of crossings) {
          matchFlashTiles.set(idx, now);
        }
        matchFlashLoop.start();
      }
    }
  }

  // A fresh attempt just started: reset the time limit. Covers the Reset
  // button/harness reset()/fail-auto-reset (RESET), selecting a level from
  // the list including Random (LOAD_LEVEL, also used by performReset() for
  // randomized levels), and painting right after Finish, which silently
  // restarts a fresh attempt inside the reducer's BRUSH case.
  const startedFreshAttempt =
    action.type === 'RESET' ||
    action.type === 'LOAD_LEVEL' ||
    (action.type === 'BRUSH' && previousStatus === 'finished' && state.status === 'playing');
  if (startedFreshAttempt) {
    timerRemainingMs = timerCapForLevel(state.level);
    timerWarningsFired.clear();
    updateTimer();
  }
  render();
  scheduleFailReset();
  return snapshot();
}

/**
 * Ticks the per-attempt time limit down by real elapsed time while
 * 'playing' (paused otherwise), dispatching TIMEOUT once it runs out, and
 * refreshes the displayed "Time: m:ss" label. Runs on its own interval,
 * independent of render(), since it's a wall-clock concern rather than
 * part of the pure game state.
 */
function updateTimer(): void {
  const now = performance.now();
  const dt = now - lastTimerTickMs;
  lastTimerTickMs = now;
  if (state.status === 'playing') {
    timerRemainingMs = Math.max(0, timerRemainingMs - dt);
    // Check warning thresholds
    for (const threshold of TIMER_WARNING_THRESHOLDS) {
      if (timerRemainingMs <= threshold && !timerWarningsFired.has(threshold)) {
        timerWarningsFired.add(threshold);
        fireTimerWarning();
      }
    }
    if (timerRemainingMs <= 0) {
      dispatch({ type: 'TIMEOUT' });
      maybeAutoCaptureBoard();
    }
  }
  timerLabel.textContent = `Time: ${formatElapsedTime(timerRemainingMs, timerCapForLevel(state.level))}`;
}

/** Fires a timer warning: double flash of floor+ceiling planes + CSS pulse on timer label. */
function fireTimerWarning(): void {
  // CSS pulse on the timer label
  timerLabel.classList.remove('timer-warning');
  // Force reflow to restart animation
  void timerLabel.offsetWidth;
  timerLabel.classList.add('timer-warning');
  setTimeout(() => timerLabel.classList.remove('timer-warning'), 600);

  // Double flash of floor+ceiling planes via a brief warning animation
  warningFlash.restart();
}


/** Renders with a warning flash on both floor and ceiling planes. */
function renderWithWarningFlash(intensity: number): void {
  const matchFlash = getMatchFlashState();
  const bottomPadding = fullframe ? controlsPrimary.offsetHeight + 32 : 0;
  renderGrid(ctx as CanvasRenderingContext2D, board.width, board.height, {
    size: state.level.size,
    heightLimit: hardHeightLimit(state.level),
    optimalHeight: showOptimalPlane ? optimalHeight(state.level.heights) : undefined,
    heights: state.heights,
    rotation: state.rotation,
    hoverTile: state.hoverTile,
    brushSize: state.brushSize,
    warningFlashIntensity: intensity,
    bottomPadding,
    zoom: state.zoom,
    tilt: state.tilt,
    ...(matchFlash ? { flashTiles: matchFlash.flashTiles, flashIntensity: matchFlash.flashIntensity } : {}),
  });
}



/** Returns the current set of flashing tile indices and a 0-1 intensity based on the freshest active flash. */
function getMatchFlashState(): { flashTiles: Set<number>; flashIntensity: number } | null {
  if (matchFlashTiles.size === 0) return null;
  const now = performance.now();
  const activeSet = new Set<number>();
  let maxIntensity = 0;
  for (const [idx, start] of matchFlashTiles) {
    const elapsed = now - start;
    if (elapsed <= MATCH_FLASH_DURATION_MS) {
      const t = 1 - elapsed / MATCH_FLASH_DURATION_MS;
      activeSet.add(idx);
      maxIntensity = Math.max(maxIntensity, t);
    }
  }
  if (activeSet.size === 0) return null;
  return { flashTiles: activeSet, flashIntensity: maxIntensity };
}

function scheduleFailReset(): void {
  if (state.status !== 'failed') {
    return;
  }
  if (failResetTimer !== null) {
    return;
  }
  isPointerDown = false;
  paintTile = null;
  paintLoop.stop();
  failFlash.restart();
  failResetTimer = setTimeout(() => {
    failResetTimer = null;
    failFlash.stop();
    performReset();
  }, FAIL_RESET_DELAY_MS);
}

/**
 * Dispatches a fresh, freshly-randomized `LOAD_LEVEL` for the "Random" level
 * (same id/name, brand new heights via `createRandomLevel(randomSeed())`),
 * or the ordinary `RESET` action for every other level. Used by the Reset
 * button, the fail-auto-reset timer, and the harness's `reset()` method, so
 * all three "start this attempt over" paths randomize consistently for the
 * Random level. The pure reducer (`reduceGame`/`RESET`) itself is untouched
 * and stays deterministic — this decision, and its `Math.random()` call,
 * live entirely here at the UI boundary.
 */
function performReset(): HarnessSnapshot {
  if (state.level.randomized) {
    return dispatch({
      type: 'LOAD_LEVEL',
      level: createRandomLevel(randomSeed(), state.level.size),
      preserveScore: true,
    });
  }
  return dispatch({ type: 'RESET' });
}

const failFlash = createFlashLoop(
  FAIL_RESET_DELAY_MS,
  (progress) => {
    const pulse = Math.abs(Math.sin(progress * Math.PI * 1));
    render(pulse);
  },
);


function render(failFlashIntensity = 0): void {
  const failFlashHeight =
    state.failedBoundary === 'ceiling'
      ? hardHeightLimit(state.level)
      : state.failedBoundary === 'floor' || state.failedBoundary === 'timeout'
        ? 0
        : null;
  const matchFlash = getMatchFlashState();
  const bottomPadding = fullframe ? controlsPrimary.offsetHeight + 32 : 0;
  renderGrid(ctx as CanvasRenderingContext2D, board.width, board.height, {
    size: state.level.size,
    heightLimit: hardHeightLimit(state.level),
    optimalHeight: showOptimalPlane ? optimalHeight(state.level.heights) : undefined,
    heights: state.heights,
    rotation: state.rotation,
    hoverTile: state.hoverTile,
    brushSize: state.brushSize,
    failFlashHeight,
    failFlashIntensity,
    bottomPadding,
    zoom: state.zoom,
    tilt: state.tilt,
    ...(matchFlash ? { flashTiles: matchFlash.flashTiles, flashIntensity: matchFlash.flashIntensity } : {}),
  });

  levelLabel.textContent = state.level.name;
  statusLabel.textContent = `Status: ${state.status}`;
  scoreLabel.textContent = `Score: ${formatScore(state.score)}`;
  efficiencyLabel.textContent = `Efficiency: ${formatScore(state.efficiency)}`;
  strokeLabel.textContent = `Moved: ${state.totalHeightChange.toFixed(1)}`;

  liftButton.setAttribute('aria-pressed', String(state.brushMode === 'lift'));
  lowerButton.setAttribute('aria-pressed', String(state.brushMode === 'lower'));

  brushSizeInput.min = String(minBrushSize());
  brushSizeInput.max = String(maxBrushSize(state.level));
  brushSizeInput.value = String(state.brushSize);
  brushSpeedInput.value = String(Math.round(state.brushSpeed * 100));
  zoomInput.value = String(Math.round(state.zoom));
  tiltInput.value = String(Math.round(state.tilt));

  const maxSize = maxBrushSize(state.level);
  const sizePadWidth = maxSize >= 10 ? 2 : 1;
  brushSizeValue.textContent = formatPadded(state.brushSize, sizePadWidth);
  brushSpeedValue.textContent = state.brushSpeed.toFixed(2);
  zoomValue.textContent = String(Math.round(state.zoom));
  tiltValue.textContent = `${Math.round(state.tilt)}°`;

  finishButton.disabled = state.status !== 'playing';
}

function persistScore(): void {
  if (state.score === null) {
    return;
  }
  session.recordScore(state.level.id, state.score);
  renderScoreboard();
}

function renderLevelList(): void {
  levelListElement.replaceChildren(
    ...baseLevels.map((level, index) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = level.name;
      button.dataset.testid = `level-select-${index}`;
      button.addEventListener('click', () => {
        dispatch({ type: 'LOAD_LEVEL', level: getLevel(index) });
      });
      item.appendChild(button);
      return item;
    }),
    createRandomLevelListItem(),
  );
}

/**
 * The "Random" level's list entry: unlike the fixed `baseLevels` entries
 * (which reload the same deterministic layout), selecting it always
 * dispatches a brand new `createRandomLevel(randomSeed())` — a fresh layout
 * every time it's chosen, including the very first time ("on first load").
 */
function createRandomLevelListItem(): HTMLLIElement {
  const item = document.createElement('li');
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = RANDOM_LEVEL_NAME;
  button.dataset.testid = 'level-select-random';
  button.addEventListener('click', () => {
    dispatch({ type: 'LOAD_LEVEL', level: createRandomLevel(randomSeed()) });
  });
  item.appendChild(button);
  return item;
}

function renderScoreboard(): void {
  scoreboardElement.replaceChildren(
    ...baseLevels.map((level) => {
      const item = document.createElement('li');
      item.textContent = `${level.name}: ${formatScore(session.getScore(level.id))}`;
      item.dataset.testid = `scoreboard-${level.id}`;
      return item;
    }),
    (() => {
      const item = document.createElement('li');
      item.textContent = `${RANDOM_LEVEL_NAME}: ${formatScore(session.getScore(RANDOM_LEVEL_ID))}`;
      item.dataset.testid = `scoreboard-${RANDOM_LEVEL_ID}`;
      return item;
    })(),
  );
}

function formatScore(score: number | null): string {
  return score === null ? '-' : `${Math.round(score * 100)}%`;
}

/** Formats a number with 2 decimal places, zero-padding the integer part to `intWidth` digits. */
function formatPadded(value: number, intWidth: number): string {
  const fixed = value.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  return intPart.padStart(intWidth, '0') + '.' + decPart;
}

function snapshot(): HarnessSnapshot {
  return serializeState(state);
}

const harness: GameHarness = {
  snapshot,
  dispatch,
  brush(x: number, y: number, mode?: BrushMode, size?: number, intensity?: number) {
    return dispatch({ type: 'BRUSH', x, y, mode, size, intensity });
  },
  setBrushMode(mode: BrushMode) {
    return dispatch({ type: 'SET_BRUSH_MODE', mode });
  },
  setBrushSize(size: number) {
    return dispatch({ type: 'SET_BRUSH_SIZE', size });
  },
  setBrushSpeed(speed: number) {
    return dispatch({ type: 'SET_BRUSH_SPEED', speed });
  },
  setZoom(zoom: number) {
    return dispatch({ type: 'SET_ZOOM', zoom });
  },
  setTilt(tilt: number) {
    return dispatch({ type: 'SET_TILT', tilt });
  },
  setHover(tile: { x: number; y: number } | null) {
    return dispatch({ type: 'SET_HOVER', tile });
  },
  rotate(direction) {
    return dispatch({ type: 'ROTATE', direction });
  },
  finish() {
    const result = dispatch({ type: 'FINISH' });
    persistScore();
    return result;
  },
  reset() {
    return performReset();
  },
  loadLevel(level: Level) {
    return dispatch({ type: 'LOAD_LEVEL', level });
  },
  timeout() {
    return dispatch({ type: 'TIMEOUT' });
  },
  togglePlane() {
    showOptimalPlane = !showOptimalPlane;
    planeToggle.setAttribute('aria-pressed', String(showOptimalPlane));
    render();
    return showOptimalPlane;
  },
  get showOptimalPlane() {
    return showOptimalPlane;
  },
};

window.__GAME_HARNESS__ = harness;


function getElement<T extends Element>(
  selector: string,
  constructor: {
    new (...args: never[]): T;
  },
): T {
  const element = document.querySelector(selector);
  if (!(element instanceof constructor)) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return element;
}

